import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  Box,
  BreadcrumbGroup,
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  Header,
  Input,
  Spinner,
} from "@amzn/awsui-components-react";
import { Board, BoardItem } from "@amzn/awsui-board-components";
import { StageContext } from "../../contexes/OrderIdContext";
import { UATProject } from "../../types/UATProject";
import {
  fetchUATProject,
  updateUATProjectAttribute,
} from "../../helpers/RavenUtils";
import { SummaryItem } from "../summary/SummaryItem";
import TestRunsView from "./TestRunsView";
import TestStatusHistoryView from "./TestStatusHistoryView";
import { BoardItemDefinition } from "@amzn/awsui-board-components/internal/interfaces";
import CurrentTestStatusView from "./CurrentTestStatusView";
import { Stage } from "../../lusca-search-api/generated-src";
import { DEFAULT_STAGE } from "../../constants";
interface BoardItemData {
  title: string;
  content: React.ReactNode | null;
}

interface BoardItemType {
  id: string;
  rowSpan: number;
  columnSpan: number;
  data: BoardItemData;
  widget?: React.ReactNode;
}

interface UATProjectViewProps {
  projectId: string;
}

export default function UATProjectView({ projectId }: UATProjectViewProps) {
  const initialItems: BoardItemDefinition<BoardItemData>[] = useMemo(
    () => [
      {
        id: "1",
        rowSpan: 6,
        columnSpan: 4,
        minRowSpan: 3,
        maxRowSpan: 12,
        minColumnSpan: 2,
        maxColumnSpan: 6,
        data: {
          title: "Test Runs",
          content: null,
        },
      },
      {
        id: "2",
        rowSpan: 4,
        columnSpan: 2,
        minRowSpan: 3,
        maxRowSpan: 12,
        minColumnSpan: 2,
        maxColumnSpan: 6,
        data: {
          title: "Test Status History",
          content: null,
        },
      },
      {
        id: "3",
        rowSpan: 4,
        columnSpan: 2,
        minRowSpan: 3,
        maxRowSpan: 12,
        minColumnSpan: 2,
        maxColumnSpan: 6,
        data: {
          title: "Current Test Status",
          content: null,
        },
      },
    ],
    []
  );
  const [items, setItems] =
    useState<BoardItemDefinition<BoardItemData>[]>(initialItems);
  const [project, setProject] = useState<UATProject | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const stageParam = (searchParams.get("stage") as Stage) || DEFAULT_STAGE;

  // Initialize items
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  // Initialize items
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectData = await fetchUATProject(projectId);
        setProject(projectData);
        if (projectData) {
          setEditedName(projectData.name);
          setEditedDescription(projectData.description);
        }
      } catch (error) {
        console.error("Error fetching project:", error);
      }
    };

    fetchData();
  }, [projectId]);

  const handleEdit = () => {
    if (project) {
      setEditedName(project.name);
      setEditedDescription(project.description);
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    if (project) {
      let success = true;
      if (editedName.trim().length > 0) {
        success =
          success &&
          updateUATProjectAttribute(project.id, "name", editedName.trim());
      }
      if (editedDescription.trim() !== project.description) {
        success =
          success &&
          updateUATProjectAttribute(
            project.id,
            "description",
            editedDescription.trim()
          );
      }

      if (success) {
        // Update the local project state with the new values
        const updatedProject = {
          ...project,
          name: editedName.trim(),
          description: editedDescription.trim(),
        };
        setProject(updatedProject);
        setIsEditing(false);
      } else {
        console.error("Failed to update project");
      }
    }
  };

  const handleCancel = () => {
    if (project) {
      setEditedName(project.name);
      setEditedDescription(project.description);
      setIsEditing(false);
    }
  };

  // Update items when project changes
  useEffect(() => {
    if (project) {
      const updatedItems: BoardItemDefinition<BoardItemData>[] = [
        {
          ...initialItems[0],
          data: {
            title: "Test Runs",
            content: project ? (
              <TestRunsView
                project={project}
                onProjectUpdate={(updatedProject) => {
                  setProject(updatedProject);
                }}
              />
            ) : null,
          },
        },
        {
          ...initialItems[1],
          data: {
            title: "Test Status History",
            content: project ? (
              <TestStatusHistoryView project={project} />
            ) : null,
          },
        },
        {
          ...initialItems[2],
          data: {
            title: "Current Test Status",
            content: project ? (
              <CurrentTestStatusView project={project} />
            ) : null,
          },
        },
      ];
      setItems(updatedItems);
    }
  }, [project, initialItems]);

  // Move createAnnouncement outside i18nStrings
  function createAnnouncement(
    operationAnnouncement: string,
    conflicts: BoardItemType[],
    disturbed: BoardItemType[]
  ) {
    const conflictsAnnouncement =
      conflicts.length > 0
        ? `Conflicts with ${conflicts.map((c) => c.data.title).join(", ")}.`
        : "";
    const disturbedAnnouncement =
      disturbed.length > 0 ? `Disturbed ${disturbed.length} items.` : "";
    return [operationAnnouncement, conflictsAnnouncement, disturbedAnnouncement]
      .filter(Boolean)
      .join(" ");
  }

  return (
    <StageContext.Provider value={stageParam}>
      {!project && (
        <Box textAlign="center">
          <Spinner size="big" />
        </Box>
      )}
      {project && (
        <AppLayout
          contentType="default"
          headerSelector="#h"
          maxContentWidth={1700}
          navigationHide
          toolsHide
          content={
            <SpaceBetween size="xl">
              <ContentLayout defaultPadding>
                <SpaceBetween size="xl">
                  <BreadcrumbGroup
                    items={[
                      { text: "UAT Projects", href: "/dashboard/uat" },
                      { text: project.name, href: `/project/${projectId}` },
                    ]}
                    ariaLabel="Breadcrumbs"
                  />
                  <Container
                    header={
                      <SpaceBetween size="m">
                        {isEditing ? (
                          // Edit Mode
                          <SpaceBetween size="m">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                              }}
                            >
                              <SpaceBetween direction="horizontal" size="s">
                                <Button onClick={handleSave} variant="primary">
                                  Save Changes
                                </Button>
                                <Button onClick={handleCancel} variant="link">
                                  Cancel
                                </Button>
                              </SpaceBetween>
                            </div>

                            <SpaceBetween size="s">
                              <div>
                                <label
                                  style={{
                                    fontWeight: "bold",
                                    marginBottom: "4px",
                                    display: "block",
                                  }}
                                >
                                  Project Name
                                </label>
                                <Input
                                  value={editedName}
                                  onChange={({ detail }) =>
                                    setEditedName(detail.value)
                                  }
                                  placeholder="Enter project name"
                                />
                              </div>

                              <div>
                                <label
                                  style={{
                                    fontWeight: "bold",
                                    marginBottom: "4px",
                                    display: "block",
                                  }}
                                >
                                  Description
                                </label>
                                <Input
                                  value={editedDescription}
                                  onChange={({ detail }) =>
                                    setEditedDescription(detail.value)
                                  }
                                  placeholder="Enter project description"
                                />
                              </div>
                            </SpaceBetween>
                          </SpaceBetween>
                        ) : (
                          // View Mode
                          <SpaceBetween size="m">
                            <Header
                              variant="h1"
                              actions={
                                <Button onClick={handleEdit} iconName="edit">
                                  Edit Project
                                </Button>
                              }
                            >
                              <SpaceBetween direction={"horizontal"} size={"m"}>
                                <span>{project.name}</span>
                                <Header variant="h3">
                                  {project.description}
                                </Header>
                              </SpaceBetween>
                            </Header>
                          </SpaceBetween>
                        )}

                        <ColumnLayout columns={4} borders="vertical">
                          <SummaryItem label="GL" value={project.GLPG} />
                          <SummaryItem
                            label="Country"
                            value={project.marketplace}
                          />
                          <SummaryItem label="Status" value={project.status} />
                          <SummaryItem
                            label="Created Date"
                            // React 18 types reject a raw Date node; stringify it.
                            value={new Date(
                              project.created_at
                            ).toLocaleString()}
                          />
                        </ColumnLayout>
                      </SpaceBetween>
                    }
                  >
                    <SpaceBetween size="xl">
                      <Board
                        renderItem={(item) => (
                          <BoardItem
                            header={<Header>{item.data.title}</Header>}
                            i18nStrings={{
                              dragHandleAriaLabel: "Drag handle",
                              dragHandleAriaDescription:
                                "Use Space or Enter to activate drag, arrow keys to move, Space or Enter to submit, or Escape to discard. Be sure to temporarily disable any screen reader navigation feature that may interfere with the functionality of the arrow keys.",
                              resizeHandleAriaLabel: "Resize handle",
                              resizeHandleAriaDescription:
                                "Use Space or Enter to activate resize, arrow keys to move, Space or Enter to submit, or Escape to discard. Be sure to temporarily disable any screen reader navigation feature that may interfere with the functionality of the arrow keys.",
                            }}
                          >
                            {item.data.content}
                          </BoardItem>
                        )}
                        // @ts-ignore
                        onItemsChange={(event: { detail: { items: any[] } }) =>
                          setItems(event.detail.items)
                        }
                        items={items}
                        i18nStrings={{
                          liveAnnouncementDndStarted: (
                            operationAnnouncement: string
                          ) => operationAnnouncement,
                          // @ts-ignore
                          liveAnnouncementDndItemReordered: (
                            operationAnnouncement: string
                          ) => operationAnnouncement,
                          liveAnnouncementDndItemCommitted: (
                            operationAnnouncement: string,
                            conflicts: Array<{ data: { title: string } }>,
                            disturbed: any[]
                          ) => {
                            const conflictsAnnouncement =
                              conflicts.length > 0
                                ? `Conflicts with ${conflicts
                                    .map((c) => c.data.title)
                                    .join(", ")}.`
                                : "";
                            const disturbedAnnouncement =
                              disturbed.length > 0
                                ? `Disturbed ${disturbed.length} items.`
                                : "";
                            return [
                              operationAnnouncement,
                              conflictsAnnouncement,
                              disturbedAnnouncement,
                            ]
                              .filter(Boolean)
                              .join(" ");
                          },
                        }}
                      />
                    </SpaceBetween>
                  </Container>
                </SpaceBetween>
              </ContentLayout>
            </SpaceBetween>
          }
        />
      )}
    </StageContext.Provider>
  );
}
